/**
 * Build-time configuration for insights-admin, validated once at module init.
 *
 * The admin SPA is a static bundle: there is no backend to ask at runtime, so
 * every environment-specific value is baked in by Vite at build time and one
 * `dist/` exists per environment. Changing the insights-api origin means a
 * rebuild, not a config edit.
 *
 * Validating at import time puts a missing variable's error next to its cause —
 * a build run without it — rather than surfacing later as a 404 against
 * `https://undefined/api/admin/clients` from inside a component. The exported
 * type is `string`, so no call site needs a guard.
 *
 * This replaces the `/api/embed/runtime-config` round trip, which existed only
 * so the SPA could ask its own backend where the API lived.
 */

/**
 * Read a required `VITE_*` variable, or throw.
 *
 * Vite statically replaces `import.meta.env.VITE_FOO` at build time, so the
 * reference must be spelled out literally — a computed lookup is *not*
 * substituted and would be `undefined` in a production build while appearing to
 * work in dev. Hence the caller passes the value in, not the name.
 */
const required = (name: string, value: string | undefined): string => {
  if (typeof value !== "string" || value.trim().length === 0) {
    throw new Error(
      `${name} is not set. insights-admin is a static bundle and reads its ` +
        `configuration at build time — set ${name} in the build environment ` +
        `and rebuild.`,
    );
  }
  return value.trim();
};

/**
 * Absolute origin of insights-api, trailing slash stripped so callers can
 * concatenate a leading-slash path without doubling up.
 *
 * Every authenticated call goes here cross-origin with the admin session
 * cookie; the login flow lives at `/api/admin/auth/*` on this same origin.
 */
export const INSIGHTS_API_URL: string = required(
  "VITE_INSIGHTS_API_URL",
  import.meta.env.VITE_INSIGHTS_API_URL as string | undefined,
).replace(/\/+$/, "");

/**
 * Header that must accompany every state-changing cookie-authenticated request.
 *
 * The value is irrelevant — its *presence* forces a CORS preflight, which
 * insights-api's origin allow-list then governs. A forged `<form>` or `<img>`
 * cannot set a custom header.
 *
 * Load-bearing because `SameSite` is no defence here: admin, dashboard and the
 * API are sibling subdomains of one registrable domain, so they are same-site
 * and the cookie rides along on cross-origin sibling requests regardless. Must
 * match `INSIGHTS_CSRF_HEADER` on the API side.
 */
export const CSRF_HEADER = "x-lucent-csrf";
